<template>
  <div class="onboarding-page">
    <div class="onboarding-wizard">
      <!-- Welcome Header -->
      <div class="welcome-container">
        <div class="welcome-header">
          <h1 class="welcome-text">Welcome to</h1>
          <div class="logo-container">
            <img
              src="/images/eddy.svg"
              alt="Eddy"
              class="logo"
            />
          </div>
        </div>
        <p class="welcome-subtitle">Let's personalize your experience</p>
      </div>

      <!-- Progress Bar -->
      <div class="progress-container">
        <div class="progress-header">
          <p class="progress-text">
            Step {{ currentStep }} of {{ totalSteps }}
          </p>
          <p class="progress-text progress-text-right">
            {{ progressPercentage }}% complete
          </p>
        </div>
        <div
          class="progress-bar"
          role="progressbar"
          :aria-valuenow="progressPercentage"
          aria-valuemin="0"
          aria-valuemax="100"
        >
          <div
            class="progress-bar-fill"
            :style="{ width: `${progressPercentage}%` }"
          ></div>
        </div>
      </div>

      <!-- Step Content -->
      <slot />
    </div>
  </div>
</template>

<script setup>
import { computed, defineProps } from "vue";

const props = defineProps({
  currentStep: {
    type: Number,
    required: true,
  },
  totalSteps: {
    type: Number,
    default: 6,
  },
});

const progressPercentage = computed(() => {
  return Math.round((props.currentStep / props.totalSteps) * 100);
});
</script>

<style scoped>
.onboarding-page {
  background-color: #f2f3fb;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px;
  min-height: 100vh;
  width: 100%;
}

.onboarding-wizard {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 32px 16px;
  width: 100%;
  max-width: 736px;
}

/* Welcome Container */
.welcome-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.welcome-header {
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
  width: 100%;
}

.welcome-text {
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: 45px;
  line-height: 52px;
  color: var(--primary-blue);
  text-align: center;
  white-space: nowrap;
  margin: 0;
}

.logo-container {
  height: 52px;
  width: 125px;
  position: relative;
}

.logo {
  height: 100%;
  width: auto;
  object-fit: contain;
}

.welcome-subtitle {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-big);
  line-height: 24px;
  color: #3b4648;
  text-align: center;
  letter-spacing: 0.024px;
  width: 100%;
  margin: 0;
}

/* Progress Container */
.progress-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  margin-bottom: 32px;
  margin-top: 32px;
}

.progress-header {
  display: flex;
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  height: 20px;
  align-items: center;
  justify-content: space-between;
  line-height: 20px;
  color: #3b4648;
  font-size: var(--fs-small);
  letter-spacing: 0.014px;
  width: 100%;
}

.progress-text {
  flex: 1 1 0;
  min-width: 0;
  margin: 0;
}

.progress-text-right {
  text-align: right;
}

.progress-bar {
  background-color: #bac1cc;
  box-sizing: border-box;
  height: 8px;
  overflow: hidden;
  border-radius: 999999px;
  width: 100%;
  position: relative;
}

.progress-bar-fill {
  background-color: var(--primary-blue);
  height: 100%;
  transition: width 0.3s ease;
  position: absolute;
  top: 0;
  left: 0;
  border-radius: 999999px;
}

/* Responsive Design */
@media (max-width: 768px) {
  .onboarding-page {
    padding: 16px;
    justify-content: flex-start;
    align-items: center;
  }

  .onboarding-wizard {
    padding: 16px 8px;
  }

  .welcome-text {
    font-size: 32px;
    line-height: 40px;
  }

  .onboarding-wizard {
    align-items: center;
  }
}
</style>
